"use client";

import { Box, Flex, Heading, useBreakpointValue } from "@chakra-ui/react";
import { ButtonProp, ImageFragment } from "lib/gcms";
import Image from "next/image";
import { Button, ButtonActions } from "@ui/components/button";
import { colors } from "@areahub/theme";
import { SectionTemplate } from "components/individuals/blocks";
import {
  Carousel,
  CarouselIconButton,
  CarouselSlide,
  useCarousel,
} from "components/individuals/blocks/featured-carousel/carousel-elements";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

interface SectorTeaserCardProps {
  displayName: string;
  image: ImageFragment & { alt?: string };
  link?: string;
  button?: ButtonProp;
}

export interface SectorTeaserCarouselProps {
  title: string;
  subtitle: string;
  bg: string;
  color: string;
  teaserCards?: SectorTeaserCardProps[];
}

export function SectorTeaserCarousel({
  title,
  subtitle,
  bg,
  color,
  teaserCards,
}: SectorTeaserCarouselProps) {
  const [sliderRef, slider] = useCarousel({
    loop: true,
    breakpoints: {
      "(min-width: 750px)": {
        slides: { perView: 2, spacing: 10 },
      },
      "(min-width: 1150px)": {
        slides: { perView: 4, spacing: 10 },
      },
    },
    slides: {
      perView: 1,
    },
  });

  return (
    <SectionTemplate {...{ bg, color }}>
      <Box textAlign="center" px={{ base: "6", md: "8" }}>
        <Heading
          as="h1"
          fontWeight="bold"
          fontSize={{
            base: 26,
            md: 34,
            lg: 38,
            xl: 45,
          }}
          lineHeight={1.2}
          textAlign={"left"}
        >
          {title}
        </Heading>
        <Heading
          as="h2"
          fontSize={{
            base: "16px",
            lg: "17px",
            xl: "18px",
          }}
          fontWeight="normal"
          mt="20px"
          textAlign={"left"}
        >
          {subtitle}
        </Heading>

        <Box position="relative" mt="48px" mb="36px">
          {/* Left arrow button */}
          <CarouselIconButton
            icon={<ChevronLeftIcon />}
            aria-controls="carousel"
            aria-label="Previous slide"
            position="absolute"
            top="50%"
            left="0"
            transform="translateY(-50%)"
            transition={"transform 0.3s ease-in-out"}
            zIndex="1"
            onClick={() => slider.current?.prev()}
          />

          {/* Carousel */}
          <Carousel
            ref={sliderRef}
            id="carousel"
            aria-label="Sector Carousel"
            shadow="xl"
          >
            {teaserCards?.map((card, idx) => (
              <CarouselSlide
                key={`teaser-cards-${idx}`}
                slideLength={teaserCards.length}
                idx={idx}
                h={{
                  base: "280px",
                  lg: "350px",
                }}
              >
                <SectorTeaserCard
                  key={`sector-teaser-card-${idx}`}
                  {...(card as SectorTeaserCardProps)}
                  color={color}
                />
              </CarouselSlide>
            ))}
          </Carousel>

          {/* Right arrow button */}
          <CarouselIconButton
            icon={<ChevronRightIcon />}
            aria-controls="carousel"
            aria-label="Next slide"
            position="absolute"
            top="50%"
            right="0"
            transform="translateY(-50%)"
            transition={"transform 0.3s ease-in-out"}
            zIndex="1"
            onClick={() => slider.current?.next()}
          />
        </Box>
      </Box>
    </SectionTemplate>
  );
}

function SectorTeaserCard({
  displayName,
  link,
  image,
  button,
  color,
}: SectorTeaserCardProps & { from?: string; color?: string }) {
  return (
    <Box
      _hover={{
        transform: "translateY(-10px) scale(1.02)",
        textDecoration: "none",
        transition: "transform 0.3s ease-in-out",
      }}
    >
      <SectorTeaserCardContent
        image={image}
        link={link as string}
        displayName={displayName}
        button={button}
        color={color}
      />
    </Box>
  );
}

function SectorTeaserCardContent({
  displayName,
  image,
  button,
  color,
}: SectorTeaserCardProps & { color?: string }) {
  const size = useBreakpointValue(
    {
      base: "80vw",
      md: "30vw",
      lg: "20vw",
    },
    { fallback: "base" },
  );
  return (
    <Flex
      flexDir={"column"}
      justify={"space-between"}
      h="full"
      color="blueGray.800"
      borderRadius="10px"
      borderWidth="1px"
      px="16px"
      pb={{
        base: "25px",
        lg: "50px",
      }}
      pt={{
        base: "25px",
        lg: "50px",
      }}
      bg="white"
      minH={{
        base: "280px",
        lg: "350px",
      }}
    >
      <Box>
        <Box display="block" width={"80px"} mx={"auto"} pb={5}>
          <Image
            {...image}
            className="post-media"
            style={{ objectFit: "contain", width: "100%", height: "auto" }}
            sizes={size}
            src={image.url}
            width={image.width}
            height={image.height}
            placeholder={
              typeof image?.blurDataURL === "string" ? "blur" : undefined
            }
            blurDataURL={
              typeof image?.blurDataURL === "string"
                ? image?.blurDataURL
                : undefined
            }
            alt={image?.alt ?? displayName}
          />
        </Box>

        <Heading
          as="h3"
          fontSize={{
            base: "lg",
            lg: "lg",
          }}
          lineHeight={"1.5"}
          mt={{
            lg: "10px",
          }}
          mb={{
            base: "8px",
            lg: "10px",
          }}
          fontWeight="bold"
          textAlign={"center"}
          color={colors.enterpriseColors.blue["300"]}
          px={{
            base: 10,
            xl: "unset",
          }}
        >
          {displayName}
        </Heading>
      </Box>
      <Button
        __type={button?.buttonAction as ButtonActions}
        bgColor={colors.enterpriseColors.blue["50"]}
        color={color}
        fontWeight={"bold"}
        fontSize={{
          base: 14,
          lg: 16,
        }}
        px={{
          base: 5,
          lg: 7,
        }}
        textTransform={"uppercase"}
        href={button?.href as string}
        label={button?.label}
      />
    </Flex>
  );
}
